import { ref, computed, watch, onUnmounted } from 'vue';

/**
 * Composable base para tabs del dashboard.
 * Gestiona AbortController, retardo mínimo de 200ms, watch de temporadas
 * y estados loading/error/data.
 *
 * @param {import('vue').Ref<number[]>} seasonIdsRef — ref con los IDs de temporada seleccionados
 * @param {import('vue').Ref<Array>} seasonsRef — ref con la lista de temporadas (para obtener nombres)
 * @param {Function} fetchFn — función que recibe (seasonIds, abortSignal) y devuelve Promise<response>
 * @returns {{
 *     data: import('vue').Ref<object|null>,
 *     loading: import('vue').Ref<boolean>,
 *     error: import('vue').Ref<string|null>,
 *     fetch: (seasonIds: number[]) => Promise<void>,
 *     isMultiSeason: import('vue').ComputedRef<boolean>,
 *     currentSeasonEntry: import('vue').ComputedRef<object|null>,
 *     seasonEntries: import('vue').ComputedRef<Array<[string, object]>>,
 *     refresh: () => Promise<void>,
 * }}
 */
export function useDashboardTab(seasonIdsRef, seasonsRef, fetchFn) {
    // ==================== Estado reactivo ====================
    const data = ref(null);
    const loading = ref(false);
    const error = ref(null);

    // AbortController para cancelación
    let abortController = null;

    // Temporizador del retardo mínimo de carga (200ms)
    let minLoadingTimer = null;
    let pollingTimer = null;
    const minLoadingElapsed = ref(true);

    // Limpieza al desmontar
    onUnmounted(() => {
        abortController?.abort();
        if (minLoadingTimer) clearTimeout(minLoadingTimer);
        if (pollingTimer) clearTimeout(pollingTimer);
    });

    // ==================== Computed ====================

    /** Indica si hay más de una temporada seleccionada */
    const isMultiSeason = computed(() => seasonIdsRef.value.length > 1);

    /** Entradas de todas las temporadas como array de [id, entry] */
    const seasonEntries = computed(() => {
        if (!data.value) return [];
        const perSeason = data.value?.data?.per_season || {};
        return Object.entries(perSeason).filter(([, entry]) => entry);
    });

    /** Datos de la primera temporada seleccionada (modo individual) */
    const currentSeasonEntry = computed(() => {
        if (!data.value || !seasonIdsRef.value.length) return null;
        const firstId = seasonIdsRef.value[0];
        return data.value?.data?.per_season?.[firstId] || null;
    });

    // ==================== Métodos ====================

    /**
     * Obtiene los datos para una o varias temporadas.
     * Cancela cualquier petición previa en curso.
     * Implementa retardo mínimo de 200ms para evitar flashes.
     *
     * @param {number[]} seasonIds
     */
    async function fetch(seasonIds) {
        // Cancelar petición previa
        abortController?.abort();
        abortController = new AbortController();

        // Retardo mínimo de 200ms
        minLoadingElapsed.value = false;
        if (minLoadingTimer) clearTimeout(minLoadingTimer);
        minLoadingTimer = setTimeout(() => {
            minLoadingElapsed.value = true;
        }, 200);

        loading.value = true;
        error.value = null;

        // Ocultar datos previos mientras carga
        data.value = null;

        try {
            const response = await fetchFn(seasonIds, abortController.signal);
            data.value = response.data;
        } catch (err) {
            if (err.name === 'CanceledError' || err.name === 'AbortError') return;
            error.value = err.message || 'Error al cargar los datos';
            if (process.env.NODE_ENV !== 'production') {
                console.error('Error fetching dashboard tab:', err);
            }
        } finally {
            // Esperar a que termine el retardo mínimo
            const checkMinLoading = () => {
                if (minLoadingElapsed.value) {
                    pollingTimer = null;
                    loading.value = false;
                } else {
                    pollingTimer = setTimeout(checkMinLoading, 50);
                }
            };
            checkMinLoading();
        }
    }

    /**
     * Refresca los datos con las temporadas actuales.
     */
    async function refresh() {
        if (seasonIdsRef.value.length > 0) {
            await fetch(seasonIdsRef.value);
        }
    }

    // ==================== Watchers ====================

    // Reactivar carga cuando cambien las temporadas seleccionadas
    watch(seasonIdsRef, newIds => {
        if (newIds.length > 0) {
            fetch(newIds);
        }
    }, { immediate: true });

    return {
        // Estado
        data,
        loading,
        error,

        // Computed
        isMultiSeason,
        currentSeasonEntry,
        seasonEntries,

        // Métodos
        fetch,
        refresh,
    };
}
